/**
 * @module treasury-conversation.service
 *
 * ## Overview
 * All data logic for the Treasury Intake flow. Owns three concerns:
 *
 *   1. **Type definitions** — `ConversationGuideFormData` (Sections A–E) and
 *      `ProgramNegotiationPolicyFormData` (Sections F–H).
 *   2. **Reference constants** — dropdown options, multi-select lists, and empty-form defaults.
 *   3. **Service functions** — two-phase data persistence:
 *      - `saveDraftGuide()` — saves intake progress to `treasury_conversation_responses`
 *        (is_draft = true). Does NOT touch the `corporates` table.
 *      - `convertIntakeToCorporate()` — called on final submission. Creates (or updates)
 *        the `corporates` row, links the draft intake, persists financial profile and
 *        negotiation policy. This is the ONLY place the corporates table is written from
 *        the intake flow.
 *      - `submitProgramNegotiationPolicy()` — standalone helper for upserting Sections F–H
 *        against an already-existing corporate (used by the edit flow).
 *
 * ## Two-Phase Architecture
 * ```
 * Phase 1 — Intake (draft)
 *   treasury_conversation_responses.corporate_id = NULL
 *   treasury_conversation_responses.is_draft = true
 *
 * Phase 2 — Promote to Corporate
 *   corporates row created / updated
 *   treasury_conversation_responses.corporate_id = <new id>
 *   treasury_conversation_responses.is_draft = false
 *   corporate_financial_profiles upserted (if financial data present)
 *   corporate_program_negotiation_policy inserted (if policy data present)
 * ```
 *
 * ## Table Dependencies
 * - `treasury_conversation_responses` — primary intake store; corporate_id is nullable
 * - `corporates` — populated only during Phase 2 promotion
 * - `corporate_financial_profiles` — optional; only written if WACC/CoF/DPO/Tax present
 * - `corporate_program_negotiation_policy` — optional; only written if Sections F–H filled
 */

import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";
import { logger } from "./logger";

// ─────────────────────────────────────────────────────────────────────────────
// Type Definitions
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Form data shape for the main intake wizard (Sections A–E).
 * Maps 1-to-1 with fields captured in `treasury_conversation_responses`
 * and firmographic fields sourced from `corporates`.
 */
export interface ConversationGuideFormData {
  // Section A — Company Snapshot (maps to corporates table)
  companyName: string;
  website: string;
  industry: string;
  annualRevenueRange: string;
  employeeCount: number | null;
  businessType: string;
  ownershipType: string;

  // Section B — AP Process Discovery
  estimatedAnnualAPVolume: number | null;
  currentPaymentMix: { check_pct: number; ach_pct: number; card_pct: number; wire_pct: number };
  apPainPoints: string[];
  numberOfSuppliers: number | null;

  // Section C — Financial Levers (maps to corporate_financial_profiles)
  waccRate: number | null;
  costOfFunds: number | null;
  dpoDays: number | null;
  taxRate: number | null;
  workingCapitalInterest: boolean;

  // Section D — Risk & Fraud
  annualFraudLosses: number | null;
  fraudConcernAreas: string[];
  virtualCardFraudInterest: boolean;

  // Section E — Program Goals & Timeline
  programObjective: string[];
  targetAdoptionRatePct: number | null;
  programTimelineMonths: number | null;
  isReactivation: boolean;
  reactivationFailureNotes: string;

  // Submission metadata
  notes: string;
  /**
   * If set, the Promote step will UPDATE this existing corporate rather than INSERT a new one.
   * Populated when user selects an existing corporate from the duplicate-match dialog,
   * or from the dropdown in the Promote section.
   */
  attachToCorporateId: number | null;

  /**
   * Optional source metadata used only by the V1 compatibility adapter.  The
   * legacy UI did not collect this metadata, so absent entries remain
   * explicitly unknown in the active contract rather than becoming invented
   * financial assumptions.
   */
  financialMetadata?: Partial<
    Record<
      "waccRate" | "costOfFunds" | "dpoDays" | "taxRate",
      { source?: string; effectiveAt?: string; effective_at?: string }
    >
  >;
}

/**
 * Form data shape for the Negotiation Policy (Sections F–H).
 * Maps to `corporate_program_negotiation_policy` columns.
 * Only persisted during Phase 2 (Promote to Corporate) if any policy field is filled.
 */
export interface ProgramNegotiationPolicyFormData {
  // Section F — Negotiation Boundaries & Edge Cases
  standardAccelerationDays: number | null;
  maxAccelerationDays: number | null;
  accelerationExceptionConditions: string;
  revertPenaltyTerms: string;
  enhancedAccelerationVolumeThreshold: number | null;
  partialAcceptanceAllowed: boolean | null;
  partialAcceptanceConditions: string;
  bankCustomerOutreachApproved: boolean | null;
  bankCustomerRequiresApproval: boolean;
  interchangeFloorRate: number | null;
  feeSharingAllowed: boolean | null;
  feeSharingConditions: string;
  trialPeriodAllowed: boolean | null;
  trialMinDurationDays: number | null;
  trialSuccessMetrics: string;
  competitiveMigrationApproved: boolean | null;
  levelIiIiiCapable: boolean | null;
  soleSourceHandling: string;
  priorDeclineReengagementPolicy: string;
  crossCorporateRoutingPolicy: string;

  // Section G — Communication Policy
  brandVoice: string;
  senderName: string;
  senderTitle: string;
  senderDepartment: string;
  emailSendingDomain: string;
  prohibitedLanguage: string[];
  legalReviewRequired: boolean;
  legalReviewTurnaroundDays: number | null;
  contactFrequency: string;
  approvalLevel: string;
  dncSupplierList: string[];

  // Section H — Operational Contacts & Readiness
  primaryPocContactId: number | null;
  strategyApproverName: string;
  strategyApproverEmail: string;
  exceptionAuthorityName: string;
  exceptionAuthorityEmail: string;
  erpSystem: string;
  currentSupplierCommMethod: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Reference Constants (used by form components for dropdown/chip options)
// ─────────────────────────────────────────────────────────────────────────────

/** Section B — pre-defined AP pain point tags */
export const AP_PAIN_POINTS = [
  "Late payments to suppliers",
  "Manual invoice matching",
  "Duplicate payments",
  "Slow approval workflows",
  "Poor visibility into AP spend",
  "Difficulty managing payment terms",
  "High processing cost per invoice",
  "Lack of early payment discounts",
] as const;

/** Section D — fraud category tags */
export const FRAUD_CONCERN_AREAS = [
  "Check fraud",
  "Vendor impersonation / BEC",
  "Unauthorized purchases",
  "Ghost vendors",
  "Invoice tampering",
  "Payment redirection",
] as const;

/** Section A — industry dropdown options (supplemented by live `industry_categories` table) */
export const INDUSTRY_OPTIONS = [
  "Manufacturing",
  "Healthcare",
  "Technology",
  "Retail / E-commerce",
  "Financial Services",
  "Construction",
  "Education",
  "Government",
  "Energy / Utilities",
  "Transportation / Logistics",
  "Professional Services",
  "Real Estate",
  "Other",
] as const;

/** Section A — annual revenue range options */
export const REVENUE_RANGES = [
  "Under $10M",
  "$10M – $50M",
  "$50M – $100M",
  "$100M – $500M",
  "$500M – $1B",
  "Over $1B",
] as const;

/** Section A — legal entity type options */
export const BUSINESS_TYPES = [
  "Corporation (C-Corp)",
  "S-Corporation",
  "LLC",
  "Partnership",
  "Sole Proprietorship",
  "Non-Profit",
  "Government Entity",
] as const;

/** Section A — ownership structure options */
export const OWNERSHIP_TYPES = [
  "Private Company",
  "Private Equity",
  "Public Company",
  "Venture Backed",
  "Government",
  "Other",
] as const;

/** Section E — program objective tags (order = priority rank) */
export const PROGRAM_OBJECTIVES = [
  "Working Capital Optimization",
  "Rebate Maximization",
  "Payment Modernization",
  "Fraud Reduction",
  "Process Efficiency",
] as const;

/** Section E — program timeline options in months */
export const PROGRAM_TIMELINES = [6, 12, 18, 24] as const;

/** Section G — tone of supplier communications */
export const BRAND_VOICES = ["Formal", "Professional", "Friendly", "Casual"] as const;

/** Section G — which domain outreach emails are sent from */
export const EMAIL_SENDING_DOMAINS = [
  { value: "buyer_domain", label: "Buyer Domain" },
  { value: "bank_domain", label: "Bank Domain" },
  { value: "co_branded", label: "Co-Branded" },
  { value: "platform", label: "Platform" },
] as const;

/** Section G — how frequently suppliers are contacted */
export const CONTACT_FREQUENCIES = ["Weekly", "Biweekly", "Monthly"] as const;

/** Section H — current method used to communicate with suppliers */
export const SUPPLIER_COMM_METHODS = [
  { value: "erp", label: "ERP" },
  { value: "email", label: "Email" },
  { value: "portal", label: "Portal" },
  { value: "phone", label: "Phone" },
  { value: "mixed", label: "Mixed" },
] as const;

// ─────────────────────────────────────────────────────────────────────────────
// Empty Form Defaults
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Blank policy form used to initialise Section F–H state and as a merge base
 * when rehydrating a partial draft from `corporate_program_negotiation_policy`.
 */
export const emptyPolicyForm: ProgramNegotiationPolicyFormData = {
  standardAccelerationDays: null,
  maxAccelerationDays: null,
  accelerationExceptionConditions: "",
  revertPenaltyTerms: "",
  enhancedAccelerationVolumeThreshold: null,
  partialAcceptanceAllowed: null,
  partialAcceptanceConditions: "",
  bankCustomerOutreachApproved: null,
  bankCustomerRequiresApproval: false,
  interchangeFloorRate: null,
  feeSharingAllowed: null,
  feeSharingConditions: "",
  trialPeriodAllowed: null,
  trialMinDurationDays: null,
  trialSuccessMetrics: "",
  competitiveMigrationApproved: null,
  levelIiIiiCapable: null,
  soleSourceHandling: "",
  priorDeclineReengagementPolicy: "",
  crossCorporateRoutingPolicy: "",
  brandVoice: "",
  senderName: "",
  senderTitle: "",
  senderDepartment: "",
  emailSendingDomain: "",
  prohibitedLanguage: [],
  legalReviewRequired: false,
  legalReviewTurnaroundDays: null,
  contactFrequency: "",
  approvalLevel: "",
  dncSupplierList: [],
  primaryPocContactId: null,
  strategyApproverName: "",
  strategyApproverEmail: "",
  exceptionAuthorityName: "",
  exceptionAuthorityEmail: "",
  erpSystem: "",
  currentSupplierCommMethod: "",
};

// ─────────────────────────────────────────────────────────────────────────────
// Internal Helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Builds the flat payload object written to `treasury_conversation_responses`.
 * Extracted to avoid duplication between insert (new draft) and update (existing draft).
 */
function buildResponsePayload(
  data: ConversationGuideFormData,
  completedBy: string,
  isDraft: boolean,
  corporateId: number | null
) {
  return {
    corporate_id: corporateId,
    completed_by: completedBy,
    is_draft: isDraft,
    status: isDraft ? "draft" : "completed",
    // Section A — Company Snapshot (self-contained on intake row; no corporate needed)
    company_name: data.companyName || null,
    website: data.website || null,
    industry: data.industry || null,
    annual_revenue_range: data.annualRevenueRange || null,
    employee_count: data.employeeCount,
    business_type: data.businessType || null,
    ownership_type: data.ownershipType || null,
    estimated_annual_ap_volume: data.estimatedAnnualAPVolume,
    number_of_suppliers: data.numberOfSuppliers,
    // Section B
    ap_pain_points: data.apPainPoints.length > 0 ? data.apPainPoints : null,
    current_payment_mix: data.currentPaymentMix as unknown as Json,
    // Section C — Financial Levers
    wacc_rate: data.waccRate,
    cost_of_funds: data.costOfFunds,
    dpo_days: data.dpoDays,
    tax_rate: data.taxRate,
    // Section D
    fraud_concern_areas: data.fraudConcernAreas.length > 0 ? data.fraudConcernAreas : null,
    working_capital_interest: data.workingCapitalInterest,
    virtual_card_fraud_interest: data.virtualCardFraudInterest,
    annual_fraud_losses: data.annualFraudLosses,
    // Metadata
    notes: data.notes || null,
    // Section E
    program_objective: data.programObjective.length > 0 ? data.programObjective : null,
    target_adoption_rate_pct: data.targetAdoptionRatePct,
    program_timeline_months: data.programTimelineMonths,
    is_reactivation: data.isReactivation,
    reactivation_failure_notes: data.reactivationFailureNotes || null,
  };
}

/**
 * Returns true if any financial field is present — used to gate financial profile upsert.
 */
function hasFinancialData(data: ConversationGuideFormData): boolean {
  return (
    data.waccRate != null ||
    data.costOfFunds != null ||
    data.dpoDays != null ||
    data.taxRate != null
  );
}

/**
 * Returns true if any policy field is present — used to gate negotiation policy insert.
 */
function hasPolicyData(policy: ProgramNegotiationPolicyFormData): boolean {
  return (
    policy.standardAccelerationDays != null ||
    policy.maxAccelerationDays != null ||
    !!policy.brandVoice ||
    !!policy.strategyApproverName ||
    policy.partialAcceptanceAllowed != null ||
    policy.trialPeriodAllowed != null
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Phase 1 — Draft Save
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Saves (or updates) a partial intake draft to `treasury_conversation_responses`.
 *
 * - Does NOT create or touch the `corporates` table.
 * - `corporate_id` remains null until `convertIntakeToCorporate()` is called.
 * - If `existingDraftId` is provided, performs an UPDATE; otherwise INSERTs a new row.
 * - Returns the `draftId` so the component can pass it back on subsequent saves.
 *
 * @throws if company name is empty, or if the Supabase upsert fails.
 */
export async function saveDraftGuide(
  data: ConversationGuideFormData,
  policy: ProgramNegotiationPolicyFormData,
  completedBy: string,
  existingDraftId?: string
): Promise<{ draftId: string }> {
  if (!data.companyName.trim()) {
    throw new Error("Company name is required to save a draft.");
  }

  if (existingDraftId) {
    // Updating an existing guide (create-mode draft or edit-mode guide).
    // Build payload without overriding is_draft/status — preserve whatever the record already has.
    const { is_draft: _id, status: _s, ...updateFields } = buildResponsePayload(
      data,
      completedBy,
      true, // isDraft arg is ignored — fields are stripped below
      data.attachToCorporateId ?? null
    );
    const { data: updated, error } = await supabase
      .from("treasury_conversation_responses")
      .update(updateFields as never)
      .eq("id", existingDraftId)
      .select("id")
      .single();

    if (error || !updated) {
      logger.error("Failed to update treasury guide", error);
      throw error || new Error("Guide row not found or no permission");
    }
    return { draftId: updated.id };
  }

  // New draft — insert with is_draft = true
  const payload = buildResponsePayload(data, completedBy, true, data.attachToCorporateId ?? null);

  const { data: inserted, error } = await supabase
    .from("treasury_conversation_responses")
    .insert(payload as never)
    .select("id")
    .single();

  if (error || !inserted) {
    logger.error("Failed to insert treasury draft", error);
    throw error || new Error("Could not save draft");
  }
  return { draftId: inserted.id };
}

// ─────────────────────────────────────────────────────────────────────────────
// Phase 2 — Promote to Corporate
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Promotes a completed intake to a full corporate record.
 * This is the ONLY function in the Treasury Intake flow that writes to `corporates`.
 *
 * ### Steps executed in order:
 * 1. Create a new `corporates` row (or update an existing one if `attachToCorporateId` is set).
 * 2. Upsert `corporate_financial_profiles` if any financial field was captured.
 * 3. Link the draft row to the new corporate and mark `is_draft = false`.
 *    If no draft existed, insert a completed response row directly.
 * 4. Insert a new `corporate_program_negotiation_policy` (deactivating the previous one)
 *    if any Sections F–H data was filled.
 *
 * @returns the `corporateId` of the created/updated corporate record.
 * @throws on any Supabase write failure (caller shows a toast with the message).
 */
export async function convertIntakeToCorporate(
  data: ConversationGuideFormData,
  policy: ProgramNegotiationPolicyFormData,
  completedBy: string,
  existingDraftId?: string
): Promise<{ corporateId: number }> {
  // ── Step 1: Corporate record ──────────────────────────────────────────────
  let corporateId = data.attachToCorporateId;

  if (corporateId) {
    // Attach mode: enrich an existing corporate with intake-captured firmographics
    const { error } = await supabase
      .from("corporates")
      .update({
        industry: data.industry || undefined,
        annual_revenue_range: data.annualRevenueRange || undefined,
        employee_count: data.employeeCount,
        business_type: data.businessType || undefined,
        ownership_type: data.ownershipType || undefined,
        annual_fraud_losses: data.annualFraudLosses,
      })
      .eq("id", corporateId);

    if (error) {
      logger.error("Failed to update corporate during promotion", error);
      throw error;
    }
  } else {
    // Create mode: new corporate from intake firmographic data
    const { data: created, error } = await supabase
      .from("corporates")
      .insert({
        name: data.companyName.trim(),
        website: data.website ? data.website.trim().replace(/\/+$/, "") : null,
        industry: data.industry || null,
        annual_revenue_range: data.annualRevenueRange || null,
        employee_count: data.employeeCount,
        business_type: data.businessType || null,
        ownership_type: data.ownershipType || null,
        annual_fraud_losses: data.annualFraudLosses,
        buyer_stage: "prospect",
        status: "Inactive",
        is_platform_user: true,
      })
      .select("id")
      .single();

    if (error || !created) {
      logger.error("Failed to create corporate during promotion", error);
      throw error || new Error("No corporate returned");
    }
    corporateId = created.id;
  }

  // ── Step 2: Financial profile (conditional) ───────────────────────────────
  if (hasFinancialData(data)) {
    const { data: existing } = await supabase
      .from("corporate_financial_profiles")
      .select("id")
      .eq("corporate_id", corporateId)
      .eq("is_active", true)
      .maybeSingle();

    const financialPayload = {
      corporate_id: corporateId!,
      wacc_rate: data.waccRate,
      cost_of_funds: data.costOfFunds,
      dpo_days: data.dpoDays,
      tax_rate: data.taxRate,
      is_active: true,
    };

    if (existing) {
      await supabase.from("corporate_financial_profiles").update(financialPayload).eq("id", existing.id);
    } else {
      await supabase.from("corporate_financial_profiles").insert(financialPayload);
    }
  }

  // ── Step 3: Link (or insert) the intake response row ─────────────────────
  if (existingDraftId) {
    // Flip the existing draft to completed and link it to the new corporate
    const { error } = await supabase
      .from("treasury_conversation_responses")
      .update({ corporate_id: corporateId!, is_draft: false, status: "completed" } as never)
      .eq("id", existingDraftId);

    if (error) {
      logger.error("Failed to promote draft conversation response", error);
      throw error;
    }
  } else {
    // No prior draft — write a completed response row directly
    const { error } = await supabase
      .from("treasury_conversation_responses")
      .insert(buildResponsePayload(data, completedBy, false, corporateId!) as never);

    if (error) {
      logger.error("Failed to save conversation response during promotion", error);
      throw error;
    }
  }

  // ── Step 4: Negotiation policy (conditional) ──────────────────────────────
  if (hasPolicyData(policy)) {
    // Deactivate any prior policy before inserting the new one
    await supabase
      .from("corporate_program_negotiation_policy")
      .update({ is_active: false })
      .eq("corporate_id", corporateId!)
      .eq("is_active", true);

    await supabase.from("corporate_program_negotiation_policy").insert({
      corporate_id: corporateId!,
      created_by: completedBy,
      is_active: true,
      // Section F
      standard_acceleration_days: policy.standardAccelerationDays,
      max_acceleration_days: policy.maxAccelerationDays,
      acceleration_exception_conditions: policy.accelerationExceptionConditions || null,
      revert_penalty_terms: policy.revertPenaltyTerms || null,
      enhanced_acceleration_volume_threshold: policy.enhancedAccelerationVolumeThreshold,
      partial_acceptance_allowed: policy.partialAcceptanceAllowed,
      partial_acceptance_conditions: policy.partialAcceptanceConditions || null,
      bank_customer_outreach_approved: policy.bankCustomerOutreachApproved,
      bank_customer_requires_approval: policy.bankCustomerRequiresApproval,
      interchange_floor_rate: policy.interchangeFloorRate != null ? policy.interchangeFloorRate / 100 : null,
      fee_sharing_allowed: policy.feeSharingAllowed,
      fee_sharing_conditions: policy.feeSharingConditions || null,
      trial_period_allowed: policy.trialPeriodAllowed,
      trial_min_duration_days: policy.trialMinDurationDays,
      trial_success_metrics: policy.trialSuccessMetrics || null,
      competitive_migration_approved: policy.competitiveMigrationApproved,
      level_ii_iii_capable: policy.levelIiIiiCapable,
      sole_source_handling: policy.soleSourceHandling || null,
      prior_decliner_reengagement_policy: policy.priorDeclineReengagementPolicy || null,
      cross_corporate_routing_policy: policy.crossCorporateRoutingPolicy || null,
      // Section G
      brand_voice: policy.brandVoice || null,
      sender_name: policy.senderName || null,
      sender_title: policy.senderTitle || null,
      sender_department: policy.senderDepartment || null,
      email_sending_domain: policy.emailSendingDomain || null,
      prohibited_language: policy.prohibitedLanguage.length > 0 ? policy.prohibitedLanguage : null,
      legal_review_required: policy.legalReviewRequired,
      legal_review_turnaround_days: policy.legalReviewTurnaroundDays,
      contact_frequency: policy.contactFrequency || null,
      approval_level: policy.approvalLevel || null,
      dnc_supplier_list: policy.dncSupplierList.length > 0 ? policy.dncSupplierList : null,
      // Section H
      primary_poc_contact_id: policy.primaryPocContactId,
      strategy_approver_name: policy.strategyApproverName || null,
      strategy_approver_email: policy.strategyApproverEmail || null,
      exception_authority_name: policy.exceptionAuthorityName || null,
      exception_authority_email: policy.exceptionAuthorityEmail || null,
      erp_system: policy.erpSystem || null,
      current_supplier_comm_method: policy.currentSupplierCommMethod || null,
    });
  }

  return { corporateId: corporateId! };
}

// ─────────────────────────────────────────────────────────────────────────────
// Standalone Policy Upsert (Edit Flow)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Standalone upsert for the Negotiation Policy (Sections F–H) against an
 * already-existing corporate. Used by the edit flow when the intake is
 * re-opened for a corporate that was previously promoted.
 *
 * Deactivates any existing active policy before inserting the new one
 * so there is always at most one active policy per corporate.
 *
 * @returns the `policyId` of the newly inserted policy row.
 */
export async function submitProgramNegotiationPolicy(
  corporateId: number,
  data: ProgramNegotiationPolicyFormData,
  submittedBy: string
): Promise<{ policyId: string }> {
  // Rotate: deactivate previous active policy
  await supabase
    .from("corporate_program_negotiation_policy")
    .update({ is_active: false })
    .eq("corporate_id", corporateId)
    .eq("is_active", true);

  const { data: inserted, error } = await supabase
    .from("corporate_program_negotiation_policy")
    .insert({
      corporate_id: corporateId,
      created_by: submittedBy,
      is_active: true,
      // Section F
      standard_acceleration_days: data.standardAccelerationDays,
      max_acceleration_days: data.maxAccelerationDays,
      acceleration_exception_conditions: data.accelerationExceptionConditions || null,
      revert_penalty_terms: data.revertPenaltyTerms || null,
      enhanced_acceleration_volume_threshold: data.enhancedAccelerationVolumeThreshold,
      partial_acceptance_allowed: data.partialAcceptanceAllowed,
      partial_acceptance_conditions: data.partialAcceptanceConditions || null,
      bank_customer_outreach_approved: data.bankCustomerOutreachApproved,
      bank_customer_requires_approval: data.bankCustomerRequiresApproval,
      interchange_floor_rate: data.interchangeFloorRate,
      fee_sharing_allowed: data.feeSharingAllowed,
      fee_sharing_conditions: data.feeSharingConditions || null,
      trial_period_allowed: data.trialPeriodAllowed,
      trial_min_duration_days: data.trialMinDurationDays,
      trial_success_metrics: data.trialSuccessMetrics || null,
      competitive_migration_approved: data.competitiveMigrationApproved,
      level_ii_iii_capable: data.levelIiIiiCapable,
      sole_source_handling: data.soleSourceHandling || null,
      prior_decliner_reengagement_policy: data.priorDeclineReengagementPolicy || null,
      cross_corporate_routing_policy: data.crossCorporateRoutingPolicy || null,
      // Section G
      brand_voice: data.brandVoice || null,
      sender_name: data.senderName || null,
      sender_title: data.senderTitle || null,
      sender_department: data.senderDepartment || null,
      email_sending_domain: data.emailSendingDomain || null,
      prohibited_language: data.prohibitedLanguage.length > 0 ? data.prohibitedLanguage : null,
      legal_review_required: data.legalReviewRequired,
      legal_review_turnaround_days: data.legalReviewTurnaroundDays,
      contact_frequency: data.contactFrequency || null,
      approval_level: data.approvalLevel || null,
      dnc_supplier_list: data.dncSupplierList.length > 0 ? data.dncSupplierList : null,
      // Section H
      primary_poc_contact_id: data.primaryPocContactId,
      strategy_approver_name: data.strategyApproverName || null,
      strategy_approver_email: data.strategyApproverEmail || null,
      exception_authority_name: data.exceptionAuthorityName || null,
      exception_authority_email: data.exceptionAuthorityEmail || null,
      erp_system: data.erpSystem || null,
      current_supplier_comm_method: data.currentSupplierCommMethod || null,
    })
    .select("id")
    .single();

  if (error || !inserted) {
    logger.error("Failed to save negotiation policy", error);
    throw error ?? new Error("No policy returned");
  }

  return { policyId: inserted.id };
}

// ─────────────────────────────────────────────────────────────────────────────
// TI.10 — Approval & Expiry Governance
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Records a formal approval on the active negotiation policy for a corporate.
 *
 * Sets `approved_by`, `approved_at` (now), and `valid_until` (caller-supplied,
 * defaults to 1 year from now on the calling side). This is a lightweight
 * field-level UPDATE — the policy row itself is not rotated, so approval
 * history is preserved within the same policy version.
 *
 * @param policyId     - UUID of the `corporate_program_negotiation_policy` row to approve.
 * @param approvedBy   - Full name (or email fallback) of the approving user.
 * @param validUntil   - ISO date string for the policy expiry date (YYYY-MM-DD).
 */
export async function approveNegotiationPolicy(
  policyId: string,
  approvedBy: string,
  validUntil: string
): Promise<void> {
  const { error } = await supabase
    .from("corporate_program_negotiation_policy")
    .update({
      approved_by: approvedBy,
      approved_at: new Date().toISOString(),
      valid_until: validUntil,
    })
    .eq("id", policyId);

  if (error) {
    logger.error("Failed to approve negotiation policy", error, { policyId });
    throw error;
  }
}

/**
 * Renews (re-approves) an existing policy by updating the `valid_until` date
 * and re-recording the approver. Used when a policy has expired or is expiring
 * soon and the approver wants to extend its validity without a full re-intake.
 *
 * Semantically identical to `approveNegotiationPolicy` — separated so call
 * sites can be distinguished for analytics and audit trail purposes.
 *
 * @param policyId     - UUID of the `corporate_program_negotiation_policy` row to renew.
 * @param approvedBy   - Full name (or email fallback) of the renewing user.
 * @param newValidUntil - ISO date string for the new expiry date (YYYY-MM-DD).
 */
export async function renewNegotiationPolicy(
  policyId: string,
  approvedBy: string,
  newValidUntil: string
): Promise<void> {
  const { error } = await supabase
    .from("corporate_program_negotiation_policy")
    .update({
      approved_by: approvedBy,
      approved_at: new Date().toISOString(),
      valid_until: newValidUntil,
    })
    .eq("id", policyId);

  if (error) {
    logger.error("Failed to renew negotiation policy", error, { policyId });
    throw error;
  }
}
